import React, { useState, useEffect } from 'react';
import {
  X, Save, AlertCircle, Info, Brain, Code, Palette, MessageCircle, Zap, Search, BrainCircuit,
  Image as ImageIcon, Lock, Check,
} from 'lucide-react';
import { apiService } from '../../services/apiService';

interface AIModel {
  id: string;
  providerModelId: string;
  name: string;
  provider: string;
  description?: string;
  price_per_token?: string;
  capabilities?: Record<string, number>;
  requires_premium?: boolean;
  contextWindow?: number;
  maxTokens?: number;
  supports_vision?: boolean;
  supports_streaming?: boolean;
  taskDeterminationModel?: boolean;
  category?: string;
  ranked_position?: number;
  date_of_model?: string;
  isUncensored?: boolean;
  isPrivacyCompliant?: boolean;
  isCustomEndpoint?: boolean;
  customEndpointUrl?: string;
  customApiKey?: string;
  customApiKeyEncrypted?: string;
  customHeaders?: Record<string, string>;
  [key: string]: any;
}

interface ModelEditModalProps {
  model: AIModel | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (model: AIModel) => Promise<string | null>;
  isAdmin: boolean;
  existingModels?: AIModel[];
}

type TabType = 'basic' | 'capabilities' | 'endpoint';

export default function ModelEditModal({ model, isOpen, onClose, onSave, isAdmin, existingModels = [] }: ModelEditModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('basic');
  const [editedModel, setEditedModel] = useState<AIModel | null>(null);
  const [error, setError] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [connectionError, setConnectionError] = useState<string>('');
  useEffect(() => {
    if (isOpen && model) {
      const processedModel = {
        ...model,
        capabilities: typeof model.capabilities === 'string'
          ? JSON.parse(model.capabilities)
          : model.capabilities || {},
        customHeaders: typeof model.customHeaders === 'string'
          ? JSON.parse(model.customHeaders)
          : model.customHeaders || {},
      };
      setEditedModel(processedModel);
      setActiveTab('basic');
      setError('');
    }
  }, [isOpen, model]);

  const handleFieldChange = (field: string, value: any) => {
    if (!editedModel) return;

    if (field === 'providerModelId') {
      setEditedModel({ ...editedModel, id: value, providerModelId: value });
      return;
    }

    setEditedModel({ ...editedModel, [field]: value });
  };

  const handleCapabilityChange = (capability: string, value: number) => {
    if (!editedModel) return;

    setEditedModel({
      ...editedModel,
      capabilities: {
        ...editedModel.capabilities,
        [capability]: value
      }
    });
  };

  const handleTestConnection = async () => {
    if (!editedModel || !editedModel.customEndpointUrl || !editedModel.customApiKey) {
      setConnectionError('Please provide both endpoint URL and API key');
      return;
    }

    setTestingConnection(true);
    setConnectionStatus('idle');
    setConnectionError('');

    try {
      await apiService.post('/portal/models/test-connection', {
        url: editedModel.customEndpointUrl,
        apiKey: editedModel.customApiKey,
        headers: editedModel.customHeaders,
        modelId: editedModel.providerModelId || 'test'
      });

      setConnectionStatus('success');
    } catch (err: any) {
      setConnectionStatus('error');
      const errorMessage = err.response?.data?.error || err.message || 'Connection failed';
      setConnectionError(errorMessage);
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSave = async () => {
    if (!editedModel) return;

    if (!editedModel.name || !editedModel.providerModelId) {
      setError('Name and Provider Model ID are required');
      return;
    }

    if (editedModel.ranked_position != null) {
      const conflict = existingModels.find(
        m => m.id !== editedModel.id && m.ranked_position === editedModel.ranked_position
      );
      if (conflict) {
        setError(`Rank ${editedModel.ranked_position} is already used by "${conflict.name}". Choose a different rank.`);
        return;
      }
    }

    if (editedModel.provider === 'custom' && (!editedModel.customEndpointUrl || !editedModel.customApiKey)) {
      setError('Custom endpoint URL and API key are required for custom providers');
      return;
    }

    setSaving(true);
    setError('');

    const errorMessage = await onSave(editedModel);

    setSaving(false);

    if (!errorMessage) {
      onClose();
    } else {
      setError(errorMessage);
    }
  };

  if (!isOpen || !editedModel) return null;

  const tabs = editedModel.provider === 'custom'
    ? ['basic', 'capabilities', 'endpoint']
    : ['basic', 'capabilities'];

  return (
    <div className="pk-scrim" style={{ position: 'fixed', zIndex: 9999, padding: 16 }} onClick={onClose}>
      <div className="pk-modal" style={{ maxWidth: 860, height: '95vh' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="pk-modal-head" style={{ justifyContent: 'space-between' }}>
          <div className="col">
            <h2 className="pk-h3">Edit Model</h2>
            <p className="pk-sub">{editedModel.name}</p>
          </div>
          <button type="button" onClick={onClose} className="pk-btn pk-btn-quiet pk-btn-icon"><X size={20} /></button>
        </div>

        {/* Tab Navigation */}
        <div className="pk-tabs" style={{ padding: '0 22px' }}>
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab as TabType)}
              className={`pk-tab ${activeTab === tab ? 'is-active' : ''}`}
              style={{ background: 'none', border: 0, cursor: 'pointer', padding: '12px 0', textTransform: 'capitalize' }}
            >
              {tab === 'endpoint' ? 'Custom Endpoint' : tab}
            </button>
          ))}
        </div>

        {/* Error Display */}
        {error && (
          <div className="pk-note pk-note-danger" style={{ margin: '16px 22px 0' }}>
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Content */}
        <div className="pk-modal-body" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {activeTab === 'basic' && (
            <BasicTab model={editedModel} onChange={handleFieldChange} isAdmin={isAdmin} />
          )}

          {activeTab === 'capabilities' && (
            <CapabilitiesTab
              capabilities={editedModel.capabilities || {}}
              onChange={handleCapabilityChange}
            />
          )}

          {activeTab === 'endpoint' && editedModel.provider === 'custom' && (
            <EndpointTab
              model={editedModel}
              onChange={handleFieldChange}
              onTestConnection={handleTestConnection}
              testingConnection={testingConnection}
              connectionStatus={connectionStatus}
              error={connectionError}
            />
          )}
        </div>

        {/* Footer */}
        <div className="pk-modal-foot">
          <div className="spacer" />
          <button type="button" onClick={onClose} className="pk-btn pk-btn-outline">Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving} className="pk-btn pk-btn-ink">
            <Save size={16} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Tab Components
function BasicTab({ model, onChange, isAdmin }: any) {
  const [showTooltip, setShowTooltip] = useState<string | null>(null);

  const tooltips: Record<string, string> = {
    name: 'A friendly display name for this model',
    providerModelId: 'Unique identifier used by the provider (e.g., gpt-4, claude-3-sonnet)',
    contextWindow: 'Maximum number of tokens the model can process in input',
    maxTokens: 'Maximum number of tokens the model can generate in output',
    category: 'Model category for filtering and organization',
    ranked_position: 'Global ranking position (1 = best)',
    price_per_token: 'Pricing format: "$1.25/1M in, $10/1M out" or "free". System averages input and output costs for price priority sorting.',
    vision: 'Model can process and understand images',
    streaming: 'Model supports real-time streaming responses',
    functionCalling: 'Model supports OpenAI-compatible tool/function calling (required for tools like OpenClaw)',
    privacy: 'Model is privacy-compliant for sensitive data',
    uncensored: 'Model handles sensitive content without restrictions',
    taskDet: 'Use this model for task determination decisions',
    premium: 'Model requires premium subscription',
  };

  const InfoIcon = ({ field }: { field: string; position?: "above" | "below" }) => (
    <span style={{ position: 'relative', display: 'inline-flex', marginLeft: 4 }}>
      <Info
        size={13}
        className="pk-i"
        style={{ cursor: 'help' }}
        onMouseEnter={() => setShowTooltip(field)}
        onMouseLeave={() => setShowTooltip(null)}
      />
      {showTooltip === field && (
        <span
          className="pk-small"
          style={{
            position: 'absolute', left: 0, top: 20, zIndex: 60, width: 224, padding: '7px 9px',
            background: 'var(--arc-ink)', color: 'var(--arc-paper)', borderRadius: 'var(--arc-r-sm)',
            boxShadow: 'var(--arc-sh-2)', lineHeight: 1.5,
          }}
        >
          {tooltips[field]}
        </span>
      )}
    </span>
  );

  return (
    <div className="pk-form-grid">
      {/* Left Column */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="pk-field">
          <label className="pk-field-label">Model Name <span className="req">*</span><InfoIcon field="name" /></label>
          <input type="text" value={model.name} onChange={(e) => onChange('name', e.target.value)} placeholder="GPT-4 Turbo" className="pk-input" />
        </div>

        <div className="pk-field">
          <label className="pk-field-label">Provider Model ID <span className="req">*</span><InfoIcon field="providerModelId" /></label>
          <input type="text" value={model.providerModelId} onChange={(e) => onChange('providerModelId', e.target.value)} placeholder="gpt-4-turbo" className="pk-input" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="pk-field">
            <label className="pk-field-label">Category<InfoIcon field="category" /></label>
            <select value={model.category || ''} onChange={(e) => onChange('category', e.target.value)} className="pk-select">
              <option value="">Select...</option>
              <option value="general">General</option>
              <option value="reasoning">Reasoning</option>
              <option value="coding">Coding</option>
              <option value="creative">Creative</option>
              <option value="vision">Vision</option>
            </select>
          </div>

          <div className="pk-field">
            <label className="pk-field-label">Rank Position<InfoIcon field="ranked_position" /></label>
            <input
              type="number"
              value={model.ranked_position ?? ''}
              onChange={(e) => onChange('ranked_position', e.target.value === '' ? undefined : parseInt(e.target.value))}
              placeholder="999"
              className="pk-input"
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="pk-field">
            <label className="pk-field-label">Context Window<InfoIcon field="contextWindow" /></label>
            <input type="number" value={model.contextWindow || ''} onChange={(e) => onChange('contextWindow', parseInt(e.target.value))} placeholder="8192" className="pk-input" />
          </div>

          <div className="pk-field">
            <label className="pk-field-label">Max Tokens<InfoIcon field="maxTokens" /></label>
            <input type="number" value={model.maxTokens || ''} onChange={(e) => onChange('maxTokens', parseInt(e.target.value))} placeholder="4096" className="pk-input" />
          </div>
        </div>

        <div className="pk-field">
          <label className="pk-field-label">Price/Token<InfoIcon field="price_per_token" /></label>
          <input type="text" value={model.price_per_token || ''} onChange={(e) => onChange('price_per_token', e.target.value)} placeholder="$1.25/1M in, $10/1M out or Free" className="pk-input" />
        </div>
      </div>

      {/* Right Column */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="pk-field">
          <label className="pk-field-label">Provider</label>
          <select value={model.provider} onChange={(e) => onChange('provider', e.target.value)} disabled={!isAdmin} className="pk-select">
            {isAdmin && <option value="openrouter">OpenRouter</option>}
            {isAdmin && <option value="google">Google</option>}
            {isAdmin && <option value="venice">Venice</option>}
            <option value="custom">Custom Endpoint</option>
          </select>
          {!isAdmin && <p className="pk-help" style={{ marginTop: 4 }}>Only admins can change provider</p>}
        </div>

        <div className="pk-field">
          <label className="pk-field-label">Description</label>
          <textarea
            value={model.description || ''}
            onChange={(e) => onChange('description', e.target.value)}
            placeholder="Latest GPT-4 model with improved performance and longer context..."
            className="pk-textarea"
            rows={3}
          />
        </div>

        {/* Checkboxes */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <label className="pk-check">
            <input type="checkbox" checked={model.supports_vision || false} onChange={(e) => onChange('supports_vision', e.target.checked)} />
            Vision<InfoIcon field="vision" />
          </label>
          <label className="pk-check">
            <input type="checkbox" checked={model.supports_streaming || false} onChange={(e) => onChange('supports_streaming', e.target.checked)} />
            Streaming<InfoIcon field="streaming" />
          </label>
          <label className="pk-check">
            <input type="checkbox" checked={model.functionCalling || false} onChange={(e) => onChange('functionCalling', e.target.checked)} />
            Function Calling<InfoIcon field="functionCalling" />
          </label>
          <label className="pk-check">
            <input type="checkbox" checked={model.isPrivacyCompliant || false} onChange={(e) => onChange('isPrivacyCompliant', e.target.checked)} />
            Privacy<InfoIcon field="privacy" />
          </label>
          <label className="pk-check">
            <input type="checkbox" checked={model.isUncensored || false} onChange={(e) => onChange('isUncensored', e.target.checked)} />
            Uncensored<InfoIcon field="uncensored" />
          </label>
          <label className="pk-check">
            <input type="checkbox" checked={model.taskDeterminationModel || false} onChange={(e) => onChange('taskDeterminationModel', e.target.checked)} />
            Task Det<InfoIcon field="taskDet" />
          </label>
          {isAdmin && (
            <label className="pk-check">
              <input type="checkbox" checked={model.requires_premium || false} onChange={(e) => onChange('requires_premium', e.target.checked)} />
              Premium<InfoIcon field="premium" />
            </label>
          )}
        </div>
      </div>
    </div>
  );
}

function CapabilitiesTab({ capabilities, onChange }: any) {
  const capabilityConfig = [
    { key: 'reasoning', label: 'Reasoning', icon: <Brain size={16} />, description: 'Logical analysis and problem-solving' },
    { key: 'coding', label: 'Coding', icon: <Code size={16} />, description: 'Programming and code generation' },
    { key: 'creativity', label: 'Creativity', icon: <Palette size={16} />, description: 'Creative writing and ideation' },
    { key: 'conversational', label: 'Conversational', icon: <MessageCircle size={16} />, description: 'Natural dialogue and chat' },
    { key: 'speed', label: 'Speed', icon: <Zap size={16} />, description: 'Response time and efficiency' },
    { key: 'webSearch', label: 'Web Search', icon: <Search size={16} />, description: 'Internet search capabilities' },
    { key: 'enhancedReasoning', label: 'Enhanced Reasoning', icon: <BrainCircuit size={16} />, description: 'Deep analytical thinking' },
    { key: 'imageGeneration', label: 'Image Generation', icon: <ImageIcon size={16} />, description: 'Creating images from text' },
  ];

  const leftColumn = capabilityConfig.slice(0, 4);
  const rightColumn = capabilityConfig.slice(4);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h3 className="pk-h3" style={{ marginBottom: 4 }}>Capability Scores</h3>
        <p className="pk-sub">Rate each capability from 0 (not capable) to 5 (excellent). This helps us route requests to the best model.</p>
      </div>

      <div className="pk-note pk-note-accent" style={{ marginBottom: 16 }}>
        <Info size={14} />
        <p className="pk-small"><strong>Tip:</strong> Set capabilities that match the model's strengths. Lower scores are okay - they help us choose the right model for each task.</p>
      </div>

      <div className="pk-form-grid">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {leftColumn.map(({ key, label, icon, description }) => (
            <CapabilityRating key={key} label={label} icon={icon} description={description} value={capabilities[key] || 0} onChange={(value) => onChange(key, value)} />
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {rightColumn.map(({ key, label, icon, description }) => (
            <CapabilityRating key={key} label={label} icon={icon} description={description} value={capabilities[key] || 0} onChange={(value) => onChange(key, value)} />
          ))}
        </div>
      </div>
    </div>
  );
}

interface CapabilityRatingProps {
  label: string;
  icon: React.ReactNode;
  description: string;
  value: number;
  onChange: (value: number) => void;
}

function CapabilityRating({ label, icon, description, value, onChange }: CapabilityRatingProps) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <div className="pk-i-plate">{icon}</div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--arc-ink)' }}>{label}</div>
          <div className="pk-small">{description}</div>
        </div>
      </div>

      <div className="pk-rating">
        <span className="pips">
          {[1, 2, 3, 4, 5].map(pip => (
            <button
              key={pip}
              type="button"
              className={pip <= value ? 'on' : ''}
              onClick={() => onChange(value === pip ? pip - 1 : pip)}
              aria-label={`${label} rating ${pip}`}
            >
              {pip}
            </button>
          ))}
        </span>
        <span className="val">{value}/5</span>
      </div>
    </div>
  );
}

function EndpointTab({ model, onChange, onTestConnection, testingConnection, connectionStatus, error }: any) {
  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h3 className="pk-h3" style={{ marginBottom: 4 }}>Custom Endpoint Configuration</h3>
        <p className="pk-sub">Configure your OpenAI-compatible API endpoint. Your API key will be encrypted for security.</p>
      </div>

      <div className="pk-note pk-note-accent" style={{ marginBottom: 16 }}>
        <Lock size={14} />
        <p className="pk-small">Your endpoint must be OpenAI-compatible (same request/response format)</p>
      </div>

      <div className="pk-field" style={{ marginBottom: 14 }}>
        <label className="pk-field-label">Endpoint URL <span className="req">*</span></label>
        <input
          type="url"
          value={model.customEndpointUrl || ''}
          onChange={(e) => onChange('customEndpointUrl', e.target.value)}
          placeholder="https://api.example.com/v1/chat/completions"
          autoComplete="new-password"
          name="endpoint-url"
          className="pk-input"
        />
        <p className="pk-help">Full URL including the path</p>
      </div>

      <div className="pk-field" style={{ marginBottom: 14 }}>
        <label className="pk-field-label">API Key <span className="req">*</span></label>
        <input
          type="password"
          value={model.customApiKey || ''}
          onChange={(e) => onChange('customApiKey', e.target.value)}
          placeholder="sk-..."
          autoComplete="new-password"
          name="api-key"
          className="pk-input"
        />
        <p className="pk-help">
          {model.customApiKeyEncrypted
            ? 'Leave blank to keep existing key. Enter new key to replace.'
            : 'Will be encrypted when saved'}
        </p>
      </div>

      <div className="pk-field" style={{ marginBottom: 14 }}>
        <label className="pk-field-label">Custom Headers (JSON)</label>
        <textarea
          value={typeof model.customHeaders === 'string'
            ? model.customHeaders
            : JSON.stringify(model.customHeaders || {}, null, 2)}
          onChange={(e) => onChange('customHeaders', e.target.value)}
          placeholder='{\n  "X-Custom-Header": "value"\n}'
          className="pk-textarea pk-input-mono"
          rows={4}
        />
        <p className="pk-help">Optional: Additional headers to include in API requests</p>
      </div>

      <div>
        <button type="button" onClick={onTestConnection} disabled={testingConnection || !model.customEndpointUrl || !model.customApiKey} className="pk-btn pk-btn-outline">
          {testingConnection ? (
            <><span className="pk-spinner" />Testing...</>
          ) : connectionStatus === 'success' ? (
            <><Check size={16} style={{ color: 'var(--arc-success)' }} />Connection Successful</>
          ) : (
            'Test Connection'
          )}
        </button>

        {connectionStatus === 'error' && error && (
          <div className="pk-note pk-note-danger" style={{ marginTop: 8 }}>{error}</div>
        )}
      </div>
    </div>
  );
}
